import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, PanelLeftOpen, Plus, RefreshCw, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useIsMobile } from '../hooks/useIsMobile';
import { useStore } from '../store/useStore';
import { Sheet, SheetContent, SheetTitle } from '../components/ui/sheet';
import {
  capital, claveEvento, deLocal, esRepetido, hoyEn, inicioMes, inicioSemana, isoUtc, mensajeError, minutosDe,
  nombreMes, rejillaMes, sumarDias, sumarMeses, textoDia, ymdDe, ZONA_DEFECTO,
} from '../lib/calendario';
import { prepararEventos } from '../components/calendar/disposicion';
import BarraLateral from '../components/calendar/BarraLateral';
import VistaRejilla from '../components/calendar/VistaRejilla';
import VistaMes from '../components/calendar/VistaMes';
import VistaAgenda from '../components/calendar/VistaAgenda';
import Flotante from '../components/calendar/Flotante';
import DetalleEvento from '../components/calendar/DetalleEvento';
import CreacionRapida from '../components/calendar/CreacionRapida';
import EditorEvento, { diferencias, eventoAForm } from '../components/calendar/EditorEvento';
import DialogoCalendario from '../components/calendar/DialogoCalendario';
import PanelCitas from '../components/calendar/PanelCitas';
import AuroraCalendario from '../components/calendar/AuroraCalendario';
import { usePreguntas } from '../components/calendar/Preguntas';
import '../components/calendar/cal.css';

/*
 * Orvian Calendar (mail.orvian.mx/calendar). Contrato de la API: claude/calendario.md.
 * Enlaces profundos (avisos de la campana y del push):
 *   ?evento=<id>[&ocurrencia=<recurrence_id>]  abre ese evento en su fecha
 *   ?calendario=<id>                           lo muestra y lo resalta en la barra lateral
 *   ?vista=citas                               páginas de reserva y próximas citas
 *   ?nuevo=1[&meet=1]                          abre el editor de un evento nuevo (con videollamada de Orvian Meet)
 */

const VISTAS = [
  ['dia', 'Día'],
  ['semana', 'Semana'],
  ['mes', 'Mes'],
  ['agenda', 'Agenda'],
];
const DIAS_AGENDA = 30;
const SOLO_MIOS = new Set(['recordatorios', 'color', 'disponibilidad', 'visibilidad']);

function vistaGuardada(movil) {
  try {
    const v = localStorage.getItem('zuno_cal_vista');
    if (VISTAS.some(([k]) => k === v)) return v;
  } catch (_) { /* sin almacenamiento */ }
  return movil ? 'agenda' : 'semana';
}

export default function CalendarPage() {
  const qc = useQueryClient();
  const movil = useIsMobile();
  const [params, setParams] = useSearchParams();
  const preguntas = usePreguntas();
  const yo = (useStore((st) => st.user?.email) || '').toLowerCase();

  const { data: datosCal } = useQuery({
    queryKey: ['cal-calendarios'],
    queryFn: async () => (await api.get('/calendar/calendars')).data,
    staleTime: 60000,
    refetchInterval: 120000,
    refetchOnWindowFocus: true,
  });
  const tz = datosCal?.zona || ZONA_DEFECTO;
  const calendarios = useMemo(() => datosCal?.calendarios || [], [datosCal]);
  const porId = useMemo(() => Object.fromEntries(calendarios.map((c) => [c.id, c])), [calendarios]);
  const principal = calendarios.find((c) => c.es_mio && c.principal);
  // Principal ELEGIDO (PUT /calendar/predeterminado, 2026-10-08): donde caen los eventos nuevos. Puede ser uno
  // compartido conmigo en el que pueda hacer cambios; sin elegir, el principal propio.
  const predeterminado = (datosCal?.predeterminado && porId[datosCal.predeterminado]) || principal;

  const hoy = hoyEn(tz);
  const [vista, setVistaEstado] = useState(() => vistaGuardada(movil));
  const [cursor, setCursor] = useState(() => hoyEn(ZONA_DEFECTO));
  const [lateralMovil, setLateralMovil] = useState(false);
  const [resaltado, setResaltado] = useState(null);
  const [seleccion, setSeleccion] = useState(null);     // creación rápida {ymd, m0, m1, todoElDia, rect}
  const [detalle, setDetalle] = useState(null);         // {ev, rect}
  const [aurora, setAurora] = useState(false);          // panel de Aurora (IA del calendario)
  // Calendario fijado al abrir (PUT /calendar/fijado, el mismo en la app): se ve solo ese hasta «Ver todos»
  const [soloFijado, setSoloFijado] = useState(true);
  const [editor, setEditor] = useState(null);           // {original, inicial}
  const [dialogoCal, setDialogoCal] = useState(null);   // {calendario|null, pestana}
  const [respondiendo, setRespondiendo] = useState(false);

  // El cursor arranca en el "hoy" de la zona de la persona en cuanto se conoce
  useEffect(() => { if (datosCal?.zona) setCursor((c) => (c === hoyEn(ZONA_DEFECTO) ? hoyEn(datosCal.zona) : c)); }, [datosCal?.zona]);
  useEffect(() => { document.title = 'Calendario · Orvian'; }, []);

  const setVista = (v) => {
    setVistaEstado(v);
    if (v !== 'citas') { try { localStorage.setItem('zuno_cal_vista', v); } catch (_) { /* nada */ } }
  };

  /* ---------------- rango visible ---------------- */
  const dias = useMemo(() => {
    if (vista === 'dia') return [cursor];
    if (vista === 'semana') { const l = inicioSemana(cursor); return Array.from({ length: 7 }, (_, i) => sumarDias(l, i)); }
    return [];
  }, [vista, cursor]);
  const [d0, d1] = useMemo(() => {
    if (vista === 'dia') return [cursor, sumarDias(cursor, 1)];
    if (vista === 'semana') { const l = inicioSemana(cursor); return [l, sumarDias(l, 7)]; }
    if (vista === 'mes') { const r = rejillaMes(cursor); return [r[0], sumarDias(r[41], 1)]; }
    return [cursor, sumarDias(cursor, DIAS_AGENDA)];
  }, [vista, cursor]);
  const desde = isoUtc(deLocal(d0, 0, tz));
  const hasta = isoUtc(deLocal(d1, 0, tz));

  const { data: eventos = [], isFetching, refetch } = useQuery({
    queryKey: ['cal-eventos', desde, hasta],
    queryFn: async () => (await api.get('/calendar/events', { params: { desde, hasta } })).data.eventos || [],
    enabled: !!datosCal && vista !== 'citas',
    placeholderData: keepPreviousData,
    // Se actualiza solo (2026-10-06): cada minuto y al volver a la pestaña
    refetchInterval: 60000,
    refetchOnWindowFocus: true,
  });

  const colorDe = useCallback((ev) => {
    if (ev.color) return ev.color;
    if (ev.invitacion) return principal?.color || '#2563eb';
    return porId[ev.calendario_id]?.color || principal?.color || '#2563eb';
  }, [porId, principal]);

  const fijado = datosCal?.fijado && porId[datosCal.fijado] ? porId[datosCal.fijado] : null;
  const filtroFijado = soloFijado ? fijado : null;
  const visibles = useMemo(() => eventos.filter((ev) => {
    if (filtroFijado) return ev.calendario_id === filtroFijado.id || (ev.invitacion && filtroFijado.id === principal?.id);
    if (ev.invitacion) return !principal?.oculto;
    const c = porId[ev.calendario_id];
    return !c || !c.oculto;
  }), [eventos, porId, principal, filtroFijado]);
  const fijar = async (c) => {
    const id = c?.id || null;
    qc.setQueryData(['cal-calendarios'], (d) => (d ? { ...d, fijado: id } : d));
    setSoloFijado(true);
    try {
      await api.put('/calendar/fijado', { calendario_id: id });
      toast.success(id ? `Al abrir verás solo «${c.nombre}»` : 'Al abrir verás todos tus calendarios');
    } catch (e) {
      toast.error(mensajeError(e, 'No se pudo fijar el calendario'));
      qc.invalidateQueries({ queryKey: ['cal-calendarios'] });
    }
  };
  const hacerPrincipal = async (c) => {
    const id = c.id === principal?.id ? null : c.id;
    const previo = datosCal?.predeterminado ?? null;
    qc.setQueryData(['cal-calendarios'], (d) => (d ? { ...d, predeterminado: id } : d));
    try {
      await api.put('/calendar/predeterminado', { calendario_id: id });
      toast.success(`«${c.nombre}» es tu calendario principal: los eventos nuevos se guardarán ahí`);
    } catch (e) {
      qc.setQueryData(['cal-calendarios'], (d) => (d ? { ...d, predeterminado: previo } : d));
      toast.error(mensajeError(e, 'No se pudo cambiar el calendario principal'));
    }
  };
  const preparados = useMemo(() => prepararEventos(visibles, tz), [visibles, tz]);

  /* ---------------- navegación ---------------- */
  const mover = (n) => {
    if (vista === 'dia') setCursor((c) => sumarDias(c, n));
    else if (vista === 'semana') setCursor((c) => sumarDias(c, 7 * n));
    else if (vista === 'mes') setCursor((c) => inicioMes(sumarMeses(c, n)));
    else setCursor((c) => sumarDias(c, DIAS_AGENDA * n));
  };
  const titulo = useMemo(() => {
    if (movil && vista !== 'citas') {
      // En el teléfono no cabe «Miércoles, 14 de octubre de 2026»
      if (vista === 'dia') return capital(textoDia(cursor, { weekday: 'short', day: 'numeric', month: 'short' }).replace(/\./g, ''));
      return capital(textoDia(vista === 'semana' ? dias[3] : cursor, { month: 'short', year: 'numeric' }).replace('.', ''));
    }
    if (vista === 'dia') return capital(textoDia(cursor, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }));
    if (vista === 'semana') {
      const a = dias[0]; const b = dias[6];
      if (a.slice(0, 7) === b.slice(0, 7)) return capital(nombreMes(a));
      return `${capital(textoDia(a, { month: 'short' }).replace('.', ''))} – ${textoDia(b, { month: 'short', year: 'numeric' }).replace('.', '')}`;
    }
    if (vista === 'mes' || vista === 'agenda') return capital(nombreMes(cursor));
    return 'Citas';
  }, [vista, cursor, dias, movil]);

  // Atajos: t = hoy, d/s/m/a = vistas, ←/→ = anterior/siguiente
  useEffect(() => {
    const tecla = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const el = document.activeElement;
      if (/input|textarea|select/i.test(el?.tagName) || el?.isContentEditable) return;
      if (document.querySelector('[role="dialog"]')) return;
      const k = e.key.toLowerCase();
      // «c» aquí crea un evento (K12): antes la tecla global abría un correo nuevo dentro del Calendario
      if (k === 'c') { e.preventDefault(); abrirEditorNuevo({}); return; }
      if (k === 't') setCursor(hoyEn(tz));
      else if (k === 'd') setVista('dia');
      else if (k === 's' || k === 'w') setVista('semana');
      else if (k === 'm') setVista('mes');
      else if (k === 'a') setVista('agenda');
      else if (e.key === 'ArrowLeft' || k === 'k' || k === 'p') mover(-1);
      else if (e.key === 'ArrowRight' || k === 'j' || k === 'n') mover(1);
    };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---------------- enlaces profundos ---------------- */
  useEffect(() => {
    const id = params.get('evento');
    const cal = params.get('calendario');
    const v = params.get('vista');
    if (!id && !cal && !v) return;
    if (v === 'citas') setVista('citas');
    else if (v && VISTAS.some(([k]) => k === v)) setVista(v);
    if (cal) {
      setResaltado(cal);
      if (movil) setLateralMovil(true);
      setTimeout(() => setResaltado(null), 6000);
    }
    if (id) {
      const rid = params.get('ocurrencia');
      api.get(`/calendar/events/${id}`, { params: rid ? { recurrence_id: rid } : {} })
        .then(({ data }) => {
          if (vista === 'citas' || v === 'citas') setVista(vistaGuardada(movil));
          setCursor(data.todo_el_dia ? data.fecha_inicio : ymdDe(data.inicio, datosCal?.zona || tz));
          setDetalle({ ev: data, rect: null });
        })
        .catch((e) => toast.error(mensajeError(e, 'Ese evento ya no existe')));
    }
    const limpio = new URLSearchParams(params);
    ['evento', 'ocurrencia', 'calendario', 'vista'].forEach((k) => limpio.delete(k));
    setParams(limpio, { replace: true });
  }, [params]); // eslint-disable-line react-hooks/exhaustive-deps

  // ?nuevo=1[&meet=1]: «Programar en Calendario» de Orvian Meet. Espera a tener los calendarios.
  useEffect(() => {
    if (!params.get('nuevo') || !datosCal) return;
    const conMeet = params.get('meet') === '1';
    const limpio = new URLSearchParams(params);
    ['nuevo', 'meet'].forEach((k) => limpio.delete(k));
    setParams(limpio, { replace: true });
    abrirEditorNuevo({ anadirMeet: conMeet });
  }, [params, datosCal]); // eslint-disable-line react-hooks/exhaustive-deps

  // Un calendario que llega por enlace y está oculto se enseña
  useEffect(() => {
    if (!resaltado) return;
    const c = porId[resaltado];
    if (c && c.oculto) alternar(c);
  }, [resaltado, porId]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---------------- calendarios ---------------- */
  const parchearCalendario = (id, cambios) => qc.setQueryData(['cal-calendarios'], (d) => (d ? {
    ...d, calendarios: d.calendarios.map((c) => (c.id === id ? { ...c, ...cambios } : c)),
  } : d));

  const alternar = async (c) => {
    parchearCalendario(c.id, { oculto: !c.oculto });
    try { await api.put(`/calendar/calendars/${c.id}/vista`, { oculto: !c.oculto }); } catch (e) {
      parchearCalendario(c.id, { oculto: c.oculto });
      toast.error(mensajeError(e, 'No se pudo cambiar'));
    }
  };
  const soloEste = async (c) => {
    const cambios = calendarios.filter((x) => (x.id === c.id ? x.oculto : !x.oculto));
    cambios.forEach((x) => parchearCalendario(x.id, { oculto: x.id !== c.id }));
    await Promise.all(cambios.map((x) => api.put(`/calendar/calendars/${x.id}/vista`, { oculto: x.id !== c.id }).catch(() => null)));
  };
  const colorear = async (c, color) => {
    parchearCalendario(c.id, { color });
    try { await api.put(`/calendar/calendars/${c.id}/vista`, { color }); } catch (e) { toast.error(mensajeError(e, 'No se pudo cambiar el color')); }
  };
  const quitar = async (c) => {
    const ok = await preguntas.confirmar(c.es_mio ? {
      titulo: `¿Eliminar «${c.nombre}»?`,
      mensaje: 'Se borrarán el calendario y todos sus eventos, y dejará de verse para quien lo tenga compartido. Los invitados de fuera recibirán la cancelación.',
    } : {
      titulo: `¿Quitar «${c.nombre}» de tu lista?`, mensaje: 'Podrás volver a agregarlo cuando quieras.', boton: 'Quitar',
    });
    if (!ok) return;
    try {
      await api.delete(`/calendar/calendars/${c.id}`);
      const { data } = await refetchCalendarios();
      if (!c.es_mio && data?.calendarios?.some((x) => x.id === c.id)) {
        // Está compartido conmigo: el servidor lo sigue listando. Al menos que no se vea.
        await api.put(`/calendar/calendars/${c.id}/vista`, { oculto: true }).catch(() => null);
        parchearCalendario(c.id, { oculto: true });
        toast.message('Está compartido contigo: se ocultó (quien lo comparte puede retirártelo).');
      } else {
        toast.success(c.es_mio ? 'Calendario eliminado' : 'Calendario quitado');
      }
      qc.invalidateQueries({ queryKey: ['cal-eventos'] });
    } catch (e) {
      toast.error(mensajeError(e, 'No se pudo quitar'));
    }
  };
  const refetchCalendarios = () => qc.fetchQuery({ queryKey: ['cal-calendarios'], queryFn: async () => (await api.get('/calendar/calendars')).data, staleTime: 0 });
  const agregarOtro = async (correo) => {
    try {
      await api.post('/calendar/calendars/agregar', { correo });
      await refetchCalendarios();
      qc.invalidateQueries({ queryKey: ['cal-eventos'] });
      toast.success('Calendario agregado');
      return true;
    } catch (e) {
      toast.error(mensajeError(e, 'No se pudo agregar'));
      return false;
    }
  };

  /* ---------------- eventos ---------------- */
  const editables = calendarios.filter((c) => c.mi_rol === 'editor' || c.mi_rol === 'admin');
  // Al crear: el fijado mientras se ve solo ese; si no, el principal elegido (si se está mostrando); si no, el
  // primero que se vea. Así el evento nuevo no «desaparece» en un calendario oculto.
  const editable = (c) => !!c && editables.some((x) => x.id === c.id);
  const calDefecto = ((editable(filtroFijado) && filtroFijado)
    || (editable(predeterminado) && !predeterminado.oculto && predeterminado)
    || (principal && !principal.oculto && principal)
    || editables.find((c) => !c.oculto) || predeterminado || principal)?.id;
  const recargar = () => qc.invalidateQueries({ queryKey: ['cal-eventos'] });

  const abrirEditorNuevo = (datos = {}) => {
    const ymd = datos.ymd || (vista === 'dia' || vista === 'semana' ? (dias.includes(hoy) ? hoy : dias[0]) : cursor);
    let m0 = datos.m0;
    if (m0 === undefined) {
      m0 = ymd === hoy ? Math.min(23 * 60, Math.ceil((minutosDe(Date.now(), tz) + 1) / 60) * 60) : 9 * 60;
    }
    const m1 = datos.m1 ?? m0 + 60;
    const cal = porId[datos.calendario_id] || porId[calDefecto];
    setSeleccion(null);
    setEditor({
      original: null,
      anadirMeet: !!datos.anadirMeet,
      inicial: eventoAForm({ ...datos, ymd, m0, m1 }, { tz, calendarioId: cal?.id, recordatoriosDefecto: cal?.recordatorios || [10] }),
    });
  };

  const crearRapido = async ({ titulo: t, calendario_id: calId }) => {
    const s = seleccion;
    const cuerpo = { titulo: t, calendario_id: calId, zona: tz };
    if (s.todoElDia) Object.assign(cuerpo, { todo_el_dia: true, fecha_inicio: s.ymd, fecha_fin: sumarDias(s.ymd, 1), disponibilidad: 'libre' });
    else Object.assign(cuerpo, { inicio: isoUtc(deLocal(s.ymd, s.m0, tz)), fin: isoUtc(deLocal(s.ymd, s.m1, tz)) });
    try {
      await api.post('/calendar/events', cuerpo);
      setSeleccion(null);
      toast.success('Evento guardado');
      recargar();
    } catch (e) {
      toast.error(mensajeError(e, 'No se pudo guardar'));
    }
  };

  const guardarEditor = async (payload) => {
    const original = editor.original;
    if (!original) {
      let notificar = true;
      if (payload.invitados.length) {
        const r = await preguntas.aviso('nuevo');
        if (r === null) return false;
        notificar = r;
      }
      try {
        await api.post('/calendar/events', { ...payload, notificar });
      } catch (e) {
        throw new Error(mensajeError(e, 'No se pudo guardar el evento'));
      }
      toast.success('Evento guardado');
    } else {
      const cambios = diferencias(payload, original);
      if (!Object.keys(cambios).length) { setEditor(null); return true; }
      let alcance = 'todas';
      if (esRepetido(original)) {
        alcance = await preguntas.alcance('editar');
        if (!alcance) return false;
      }
      let notificar = false;
      const tocaInvitados = Object.keys(cambios).some((k) => !SOLO_MIOS.has(k));
      if (original.soy_organizador && tocaInvitados && ((original.invitados || []).length || (payload.invitados || []).length)) {
        const r = await preguntas.aviso('cambio');
        if (r === null) return false;
        notificar = r;
      }
      try {
        await api.patch(`/calendar/events/${original.id}`, { ...cambios, alcance, recurrence_id: original.recurrence_id || undefined, notificar });
      } catch (e) {
        throw new Error(mensajeError(e, 'No se pudo guardar el evento'));
      }
      toast.success('Cambios guardados');
    }
    setEditor(null);
    setDetalle(null);
    recargar();
    return true;
  };

  const moverEvento = async (ev, { inicio, fin }) => {
    let alcance = 'todas';
    if (esRepetido(ev)) {
      alcance = await preguntas.alcance('mover');
      if (!alcance) return;
    }
    let notificar = false;
    if (ev.soy_organizador && (ev.invitados || []).length) {
      const r = await preguntas.aviso('cambio');
      if (r === null) return;
      notificar = r;
    }
    const clave = ['cal-eventos', desde, hasta];
    const previo = qc.getQueryData(clave);
    qc.setQueryData(clave, (l) => (l || []).map((x) => (claveEvento(x) === claveEvento(ev) ? { ...x, inicio: isoUtc(inicio), fin: isoUtc(fin) } : x)));
    try {
      await api.patch(`/calendar/events/${ev.id}`, {
        inicio: isoUtc(inicio), fin: isoUtc(fin), alcance, recurrence_id: ev.recurrence_id || undefined, notificar,
      });
      toast.success('Evento movido');
    } catch (e) {
      qc.setQueryData(clave, previo);
      toast.error(mensajeError(e, 'No se pudo mover'));
    }
    recargar();
  };

  const borrar = async (ev) => {
    let alcance = 'todas';
    if (esRepetido(ev)) {
      alcance = await preguntas.alcance('borrar');
      if (!alcance) return;
    } else {
      const ok = await preguntas.confirmar({
        titulo: ev.editable_parcial ? '¿Quitar este evento de tu calendario?' : '¿Eliminar este evento?',
        mensaje: ev.cita_id ? 'Es una cita reservada: se cancelará la cita.' : `«${ev.titulo || '(Sin título)'}» se borrará del calendario.`,
        boton: ev.editable_parcial ? 'Quitar' : 'Eliminar',
      });
      if (!ok) return;
    }
    let notificar = false;
    if (ev.soy_organizador && (ev.invitados || []).length && !ev.editable_parcial) {
      const r = await preguntas.aviso('borrar');
      if (r === null) return;
      notificar = r;
    }
    try {
      await api.delete(`/calendar/events/${ev.id}`, { params: { alcance, recurrence_id: ev.recurrence_id || undefined, notificar } });
      setDetalle(null);
      toast.success('Evento eliminado');
      recargar();
      if (ev.cita_id) qc.invalidateQueries({ queryKey: ['cal-citas'] });
    } catch (e) {
      toast.error(mensajeError(e, 'No se pudo eliminar'));
    }
  };

  const responder = async (estado) => {
    const ev = detalle?.ev;
    if (!ev) return;
    setRespondiendo(true);
    try {
      await api.post(`/calendar/events/${ev.id}/responder`, { estado });
      setDetalle((d) => (d ? {
        ...d,
        ev: { ...d.ev, mi_estado: estado, invitados: (d.ev.invitados || []).map((i) => (i.correo.toLowerCase() === yo ? { ...i, estado } : i)) },
      } : d));
      toast.success({ aceptado: 'Respondiste que sí', quizas: 'Respondiste que quizás', rechazado: 'Respondiste que no' }[estado]);
      recargar();
    } catch (e) {
      toast.error(mensajeError(e, 'No se pudo responder'));
    } finally {
      setRespondiendo(false);
    }
  };

  const abrirEventoPorId = async (id) => {
    try {
      const { data } = await api.get(`/calendar/events/${id}`);
      setVista(vistaGuardada(movil) === 'citas' ? 'semana' : vistaGuardada(movil));
      setCursor(data.todo_el_dia ? data.fecha_inicio : ymdDe(data.inicio, tz));
      setDetalle({ ev: data, rect: null });
    } catch (e) {
      toast.error(mensajeError(e, 'No se encontró el evento'));
    }
  };

  const puedeMover = useCallback((ev) => !!ev.editable && !ev.solo_ocupado && !ev.todo_el_dia, []);
  const irDia = (d) => { setCursor(d); setVista('dia'); };

  /* ---------------- pintar ---------------- */
  const lateral = (
    <BarraLateral
      cursor={cursor}
      hoy={hoy}
      onElegirDia={(d) => { setCursor(d); if (vista === 'citas') setVista(vistaGuardada(movil)); setLateralMovil(false); }}
      calendarios={calendarios}
      resaltado={resaltado}
      onCrear={() => { setLateralMovil(false); abrirEditorNuevo(); }}
      onCrearMeet={() => { setLateralMovil(false); abrirEditorNuevo({ anadirMeet: true }); }}
      onAlternar={alternar}
      onSoloEste={soloEste}
      fijado={fijado?.id}
      onFijar={fijar}
      predeterminado={predeterminado?.id}
      onPrincipal={hacerPrincipal}
      onColor={colorear}
      onConfigurar={(c, pestana) => setDialogoCal({ calendario: c, pestana })}
      onQuitar={quitar}
      onNuevoCalendario={() => setDialogoCal({ calendario: null, pestana: 'general' })}
      onAgregar={agregarOtro}
      vista={vista}
      onCitas={() => { setVista('citas'); setLateralMovil(false); }}
    />
  );

  const vistaComun = { tz, hoy, preparados, colorDe, onAbrir: (ev, rect) => { setSeleccion(null); setDetalle({ ev, rect }); }, onIrDia: irDia };

  return (
    <div className="cal-pagina" data-testid="calendar-page">
      {!movil && lateral}
      {movil && (
        <Sheet open={lateralMovil} onOpenChange={setLateralMovil}>
          <SheetContent side="left" className="p-0 w-[300px] cal-lateral-hoja">
            <SheetTitle className="sr-only">Calendarios</SheetTitle>
            {lateral}
          </SheetContent>
        </Sheet>
      )}

      <AuroraCalendario abierto={aurora} onCerrar={() => setAurora(false)} tz={tz}
        onCambios={() => recargar()} onAbrirEvento={(id) => abrirEventoPorId(id)} />
      <section className="cal-principal">
        <div className="cal-barra">
          {movil && (
            <button type="button" className="cal-icono-btn" onClick={() => setLateralMovil(true)} aria-label="Calendarios" data-testid="cal-abrir-lateral"><PanelLeftOpen className="h-5 w-5" /></button>
          )}
          {vista !== 'citas' && (
            <>
              <button type="button" className="od-btn-secondary cal-hoy" onClick={() => setCursor(hoyEn(tz))} data-testid="cal-hoy">Hoy</button>
              <button type="button" className="cal-icono-btn" onClick={() => mover(-1)} aria-label="Anterior" data-testid="cal-anterior"><ChevronLeft className="h-5 w-5" /></button>
              <button type="button" className="cal-icono-btn" onClick={() => mover(1)} aria-label="Siguiente" data-testid="cal-siguiente"><ChevronRight className="h-5 w-5" /></button>
            </>
          )}
          <h1 className="cal-barra-titulo" data-testid="cal-titulo">{titulo}</h1>
          {vista !== 'citas' && isFetching && <RefreshCw className="h-4 w-4 od-spin" style={{ color: 'var(--text-3)' }} aria-label="Cargando" />}
          <span className="flex-1" />
          <button type="button" className={`od-btn-secondary cal-aurora-boton ${aurora ? 'is-activa' : ''}`} onClick={() => setAurora((v) => !v)}
            aria-pressed={aurora} title="Pídele a Aurora que cree o mueva eventos" data-testid="cal-aurora-abrir">
            <Sparkles className="h-4 w-4" />{!movil && 'Aurora'}
          </button>
          {vista !== 'citas' && !movil && (
            <button type="button" className="cal-icono-btn" onClick={() => refetch()} aria-label="Actualizar" title="Actualizar"><RefreshCw className="h-4 w-4" /></button>
          )}
          {movil ? (
            <select className="cal-select" value={vista} onChange={(e) => setVista(e.target.value)} aria-label="Vista" data-testid="cal-vista-select">
              {VISTAS.map(([k, t]) => <option key={k} value={k}>{t}</option>)}
              <option value="citas">Citas</option>
            </select>
          ) : (
            <div className="cal-segmentos" role="tablist" aria-label="Vista">
              {VISTAS.map(([k, t]) => (
                <button key={k} type="button" role="tab" aria-selected={vista === k} className={`cal-segmento ${vista === k ? 'is-activa' : ''}`} onClick={() => setVista(k)} data-testid={`cal-vista-${k}`}>{t}</button>
              ))}
              <button type="button" role="tab" aria-selected={vista === 'citas'} className={`cal-segmento ${vista === 'citas' ? 'is-activa' : ''}`} onClick={() => setVista('citas')} data-testid="cal-vista-citas">Citas</button>
            </div>
          )}
        </div>

        {filtroFijado && vista !== 'citas' && (
          <div className="cal-franja-fijado" data-testid="cal-franja-fijado">
            <span className="cal-casilla" style={{ '--c': filtroFijado.color }} aria-hidden="true" />
            <span className="truncate">Solo «{filtroFijado.nombre}»</span>
            <button type="button" className="cal-mini-enlace" onClick={() => setSoloFijado(false)}>Ver todos</button>
          </div>
        )}
        <div className="cal-contenido">
          {!datosCal ? (
            <div className="cal-vacio"><p>Cargando calendario…</p></div>
          ) : vista === 'citas' ? (
            <PanelCitas tz={tz} calendarios={calendarios} principal={editable(predeterminado) ? predeterminado : principal} preguntas={preguntas} onAbrirEvento={abrirEventoPorId} />
          ) : vista === 'mes' ? (
            <VistaMes cursor={cursor} {...vistaComun} compacto={movil} onCrear={(d) => (movil ? abrirEditorNuevo({ ymd: d.ymd, todoElDia: true }) : setSeleccion(d))} />
          ) : vista === 'agenda' ? (
            <VistaAgenda desde={cursor} dias={DIAS_AGENDA} {...vistaComun} />
          ) : (
            <VistaRejilla
              dias={dias}
              {...vistaComun}
              onCrear={(d) => { setDetalle(null); setSeleccion(d); }}
              onMover={moverEvento}
              puedeMover={puedeMover}
              seleccion={seleccion}
            />
          )}
        </div>
      </section>

      {vista !== 'citas' && (
        <button type="button" className={`cal-fab ${movil ? '' : 'cal-fab-ancho'}`} onClick={() => abrirEditorNuevo()} aria-label="Crear evento" title="Crear evento" data-testid="cal-fab">
          <Plus className="h-6 w-6" />{!movil && <span>Crear</span>}
        </button>
      )}

      <Flotante abierto={!!seleccion} rect={seleccion?.rect} onCerrar={() => setSeleccion(null)} ancho={380} titulo="Nuevo evento" testId="cal-flotante-crear">
        {seleccion && (
          <CreacionRapida
            key={`${seleccion.ymd}-${seleccion.m0}-${seleccion.m1}-${seleccion.todoElDia}`}
            datos={seleccion}
            calendarios={editables}
            calendarioDefecto={calDefecto}
            onGuardar={crearRapido}
            onMas={({ titulo: t, calendario_id: calId, anadirMeet }) => abrirEditorNuevo({ ...seleccion, titulo: t, calendario_id: calId, anadirMeet })}
            onCerrar={() => setSeleccion(null)}
          />
        )}
      </Flotante>

      <Flotante abierto={!!detalle && !editor} rect={detalle?.rect} onCerrar={() => setDetalle(null)} ancho={420} titulo={detalle?.ev?.titulo || 'Evento'} testId="cal-flotante-detalle">
        {detalle && (
          <DetalleEvento
            ev={detalle.ev}
            tz={tz}
            calendario={detalle.ev.invitacion ? null : porId[detalle.ev.calendario_id]}
            color={colorDe(detalle.ev)}
            onCerrar={() => setDetalle(null)}
            onEditar={() => setEditor({ original: detalle.ev, inicial: eventoAForm(detalle.ev, { tz }) })}
            onBorrar={() => borrar(detalle.ev)}
            onResponder={responder}
            respondiendo={respondiendo}
            onDuplicar={() => {
              const f = eventoAForm(detalle.ev, { tz });
              setDetalle(null);
              setEditor({ original: null, inicial: { ...f, invitados: f.invitados.map((i) => ({ ...i, estado: 'pendiente' })) } });
            }}
          />
        )}
      </Flotante>

      {editor && (
        <EditorEvento
          abierto
          original={editor.original}
          inicial={editor.inicial}
          tz={tz}
          calendarios={editor.original && !editables.some((c) => c.id === editor.original.calendario_id) && porId[editor.original.calendario_id]
            ? [...editables, porId[editor.original.calendario_id]] : editables}
          onCerrar={() => setEditor(null)}
          onGuardar={guardarEditor}
          anadirMeet={!!editor.anadirMeet}
        />
      )}

      {dialogoCal && (
        <DialogoCalendario
          abierto
          calendario={dialogoCal.calendario ? porId[dialogoCal.calendario.id] || dialogoCal.calendario : null}
          pestanaInicial={dialogoCal.pestana}
          onCerrar={() => setDialogoCal(null)}
          onGuardado={() => { qc.invalidateQueries({ queryKey: ['cal-calendarios'] }); recargar(); }}
          preguntas={preguntas}
        />
      )}

      {preguntas.nodo}
    </div>
  );
}
